'use strict';
const angular = require('angular');
const {forEach, isDefined, isFunction, isNumber, isObject, isString, noop} = angular;

import {BUTTON_STATES} from './CONFIRM_BUTTON_STATES';

const template = require('./stateful-button.html');

module.exports.directive = [
  StatefulButtonDirective];

module.exports.controller = [
  '$scope',
  '$element',
  '$attrs',
  '$timeout',
  '$parse',
  '$translate',
  StatefulButtonController];

/**
 * Attributes
 * Currently, attributes only support fixed string inputs
 *
 *
 * Attributes required:
 * start-label
 * on-confirm($event, $state)
 *
 * Attributes supported:
 * start-style      = confirm-button-start
 * on-start         = noop($event, $state) - called when the button is pressed in start state, which automatically transitions to confirm state
 * confirm-label    = confirm.button.confirm.label
 * confirm-style    = confirm-button-confirm
 * working-label    = confirm.button.working.label
 * working-style    = confirm-button-working
 * success-label    = confirm.button.success.label
 * success-style    = confirm-button-success
 * on-success       = noop($event, $state) - called after the timeout expires or the button is pressed while in the success state
 * failure-label    = confirm.button.failure.label
 * failure-style    = confirm-button-failure
 * on-failure       = noop($event, $state) - called after the timeout expires or the button is pressed while in the failure state
 * complete-timeout = 750 - the time to show the success/failure state. if falsey, state will persist until clicked
 */
function StatefulButtonDirective() {
  return {
    restrict: 'E',
    template: template,
    scope: {
      api: '=?',
      working: '=?'
    },
    controllerAs: 'statefulButton',
    bindToController: true,
    controller: 'StatefulButtonController'
  };
}

function StatefulButtonController($scope, $element, $attrs, $timeout, $parse, $translate) {
  var vm = this;
  var isReady = false;
  var timeout = isDefined($attrs.completeTimeout) && !$attrs.completeTimeout ? 0 : (isNumber($attrs.completeTimeout) ? $attrs.completeTimeout : 750);
  var states = [];
  try {
    var ready = _initStates();
  } catch (error) {
    console.warn('StatefulButtonController failed to parse method to call. Expected expressions for on-start an on-confirm. ' + $element);
    console.error(error);
    return
  }

  vm.getState = getState;
  vm.setState = setState;
  vm.isWorking = isWorking;

  vm.api = {
    onClick: _onClick,
    getState: getState,
    setState: setState,
    onSuccess: onSuccess,
    onFailure: onFailure
  };

  vm.working = false;
  vm.flipped = false;

  setState(BUTTON_STATES.START);

  $element.on('click', _onClick);

  $scope.$on('$destroy', _cleanup);

  function getState() {
    return vm.__state;
  }

  function setState(state) {
    vm.__state = state;

    if (!isReady) {
      ready.then(function () {
        isReady = true;
        _updateState();
      });
    } else {
      $scope.$evalAsync(_updateState);
    }
  }

  function isWorking() {
    return vm.__state === BUTTON_STATES.WORKING;
  }


  // TODO: consider writing directly to the DOM instead of relying on angular bindings
  function _updateState() {
    let flip = vm.__state !== BUTTON_STATES.START;
    vm.label = states[vm.__state].label;
    vm.style = states[vm.__state].style;
    if (vm.flipped !== flip) {
      vm.flipped = flip;
      setTimeout(() => {
        if (flip)
          $element.addClass('flipped');
        else
          $element.removeClass('flipped');
      });
    }
  }

  function _onClick(e) {
    if (vm.__state === BUTTON_STATES.START) {
      $scope.$safeApply(function () {
        var result = states[BUTTON_STATES.START].handler($scope.$parent, {
          $event: e,
          $state: BUTTON_STATES.START
        });

        vm.__state = BUTTON_STATES.WORKING;
        _updateState();

        if (isObject(result) && isFunction(result.then)) {
          result.then(onSuccess, onFailure).finally(onFinally);
        } // else, the user must call
      });
    } else if (vm.__state === BUTTON_STATES.SUCCESS) {
      if (!states[BUTTON_STATES.SUCCESS].handler) {
        setState(BUTTON_STATES.START);
      } else {
        $scope.$safeApply(function () {
          states[BUTTON_STATES.SUCCESS].handler($scope.$parent, {
            $event: e,
            $state: BUTTON_STATES.SUCCESS
          });

          vm.__state = BUTTON_STATES.START;
          _updateState();
        });
      }
    } else if (vm.__state === BUTTON_STATES.FAILURE) {
      if (!states[BUTTON_STATES.FAILURE].handler) {
        setState(BUTTON_STATES.START);
      } else {
        $scope.$safeApply(function () {
          states[BUTTON_STATES.FAILURE].handler($scope.$parent, {
            $event: e,
            $state: BUTTON_STATES.FAILURE
          });

          vm.__state = BUTTON_STATES.START;
          _updateState();
        });
      }
    }
  }

  function onSuccess() {
    setState(BUTTON_STATES.SUCCESS);

    if (!states[BUTTON_STATES.SUCCESS].handler) {
      if (timeout) $timeout(setState.bind(vm, BUTTON_STATES.START), timeout);
    } else if (timeout) {
      $timeout(function () {
        $scope.$safeApply(function () {
          states[BUTTON_STATES.SUCCESS].handler($scope.$parent, {$state: BUTTON_STATES.SUCCESS});
          setState(BUTTON_STATES.START);
        });
      }, timeout);
    }
  }

  function onFailure() {
    setState(BUTTON_STATES.FAILURE);

    if (!states[BUTTON_STATES.FAILURE].handler) {
      if (timeout) $timeout(setState.bind(vm, BUTTON_STATES.START), timeout);
    } else if (timeout) {
      $timeout(function () {
        $scope.$safeApply(function () {
          states[BUTTON_STATES.FAILURE].handler($scope.$parent, {$state: BUTTON_STATES.FAILURE});
          setState(BUTTON_STATES.START);
        });
      }, timeout);
    }
  }

  function onFinally() {
    vm.working = false;
  }

  function _initStates() {
    var promises = [];
    var labels = [
      $attrs.startLabel,
      null, /* No Confirm State */
      $attrs.workingLabel || 'confirm.button.working.label',
      $attrs.successLabel || 'confirm.button.success.label',
      $attrs.failureLabel || 'confirm.button.failure.label'
    ];
    if (!labels[0]) throw new Error('StatefulButtonDirective expected string for start-label. ' + $element);

    states = [
      {
        style: $attrs.startStyle || 'stateful-button-start',
        handler: $parse($attrs.onStart)
      },
      {
        /* No Confirm State */
      },
      {
        style: isString($attrs.workingStyle) ? $attrs.workingStyle + ' icon' : 'icon stateful-button-working'
      },
      {
        style: $attrs.successStyle || 'stateful-button-success',
        handler: isString($attrs.onSuccess) ? $parse($attrs.onSuccess) : noop
      },
      {
        style: $attrs.failureStyle || 'stateful-button-failure',
        handler: isString($attrs.onfailure) ? $parse($attrs.onfailure) : noop
      }
    ];

    vm.defs = states[BUTTON_STATES.START];

    forEach(labels, function (msg, labelId) {
      if (msg === null) return;
      promises.push($translate(msg).then(function (label) {
        states[labelId].label = label;
      }));
    });

    return Promise.all(promises);
  }

  function _cleanup() {
    $element.off('click', _onClick);
  }
}



// WEBPACK FOOTER //
// ./src/js/core-components/transforming-buttons/StatefulButtonDirective.js